import type { ProgressStep } from '@localagent/sdk';
import type { CognitiveToolType } from '../../types/reasoning-types';

// ---------------------------------------------------------------------------
// Exported helpers (reused by ReasoningRunsSection in the debug panel)
// ---------------------------------------------------------------------------

export function isContextTool(toolName: string | undefined): boolean {
  return toolName != null && toolName.startsWith('context_');
}

export function isContextSearchTool(toolName: string | undefined): boolean {
  return toolName === 'context_search';
}

// ---------------------------------------------------------------------------
// Context-aware icons for tool steps
// ---------------------------------------------------------------------------

function contextSearchIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-purple-400">
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.3-4.3" />
    </svg>
  );
}

function contextPinIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-purple-400">
      <line x1="12" x2="12" y1="17" y2="22" />
      <path d="M5 17h14v-1.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V6h1a2 2 0 0 0 0-4H8a2 2 0 0 0 0 4h1v4.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24Z" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// StepEntry — renders a single planner step (exported for debug panel reuse)
// ---------------------------------------------------------------------------

export function StepEntry({ step }: { step: ProgressStep }) {
  if (step.type === 'triage') {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-green-400">
          <path d="M13 2 3 14h9l-1 8 10-12h-9l1-8z" />
        </svg>
        <span>Direct response</span>
      </div>
    );
  }

  if (step.type === 'llm_call') {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-blue-400">
          <path d="M21 12a9 9 0 1 1-6.219-8.56" />
        </svg>
        <span>LLM call (iteration {(step.iteration ?? 0) + 1})</span>
        {step.hasToolCalls && <span className="rounded bg-muted px-1 text-[10px]">tool calls</span>}
      </div>
    );
  }

  const isError = step.toolResultStatus === 'error';
  const isContext = isContextTool(step.toolName);

  // Context tools get a specialized icon and purple badge
  if (isContext) {
    const icon = isContextSearchTool(step.toolName) ? contextSearchIcon() : contextPinIcon();
    return (
      <div className="flex items-center gap-2 text-xs">
        {icon}
        <span className="font-medium text-foreground">{step.toolName}</span>
        <span className="rounded bg-purple-100 px-1 text-[10px] text-purple-700 dark:bg-purple-900/30 dark:text-purple-400">
          context
        </span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 text-xs">
      {isError ? (
        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-red-400">
          <path d="M18 6 6 18" />
          <path d="m6 6 12 12" />
        </svg>
      ) : (
        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-green-400">
          <path d="M20 6 9 17l-5-5" />
        </svg>
      )}
      <span className="font-medium text-foreground">{step.toolName}</span>
      <span className={`rounded px-1 text-[10px] ${isError ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'}`}>
        {step.toolResultStatus}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ReasoningProgress — compact inline view for chat
// ---------------------------------------------------------------------------

const COGNITIVE_PHASE_LABELS: Record<CognitiveToolType, string> = {
  explore: 'Exploring',
  research: 'Researching',
  plan: 'Planning',
  execute_plan: 'Executing',
  evaluate: 'Evaluating',
  audit: 'Auditing',
  delegate: 'Delegating',
  triage: 'Triaging',
};

// The per-event row view used to render here too, duplicating ChatView's idle
// copy under a mutual-exclusion gate. Both now live in ONE place —
// {@link AgentEventTrail}, inside ChatInterface's scroll region — so this
// component is purely the compact "Thinking… N steps" status bubble.
interface ReasoningProgressProps {
  isReasoning: boolean;
  recentSteps: ProgressStep[];
}

export function ReasoningProgress({ isReasoning, recentSteps }: ReasoningProgressProps) {
  const hasSteps = recentSteps.length > 0;

  // Detect the most recent cognitive tool call for phase indicator
  const recentCognitiveTool = (() => {
    for (let i = recentSteps.length - 1; i >= 0; i--) {
      const step = recentSteps[i];
      if (step.type === 'tool_call' && step.toolName && step.toolName in COGNITIVE_PHASE_LABELS) {
        return step.toolName as CognitiveToolType;
      }
    }
    return null;
  })();

  if (!hasSteps && isReasoning) {
    return (
      <div className="flex flex-col items-start gap-2">
        <div className="rounded-2xl rounded-bl-md px-4 py-3">
          <div className="flex gap-1">
            <span className="h-2 w-2 animate-bounce rounded-full bg-muted-foreground/50" style={{ animationDelay: '0ms' }} />
            <span className="h-2 w-2 animate-bounce rounded-full bg-muted-foreground/50" style={{ animationDelay: '150ms' }} />
            <span className="h-2 w-2 animate-bounce rounded-full bg-muted-foreground/50" style={{ animationDelay: '300ms' }} />
          </div>
        </div>
      </div>
    );
  }

  if (!hasSteps) return null;

  return (
    <div className="flex flex-col items-start gap-2">
      <div className="w-full max-w-sm rounded-2xl rounded-bl-md px-4 py-3">
        {/* Compact step summary with cognitive phase and active agents */}
        {hasSteps && (
          <div className="space-y-0.5">
            <p className="text-xs font-medium text-muted-foreground">
              {isReasoning ? 'Thinking' : 'Thought'}... {recentSteps.length} step{recentSteps.length !== 1 ? 's' : ''}
              {recentCognitiveTool && (
                <span className="ml-1 rounded bg-indigo-100 px-1 text-[10px] text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400">
                  {COGNITIVE_PHASE_LABELS[recentCognitiveTool]}
                </span>
              )}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
